import { DateTime } from 'luxon';
import React, { type FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import Button from '@ui/atoms/Button';
import { CardRow } from '@ui/atoms/CardRow';
import { DashboardInfoDisplayText } from '@ui/atoms/DashboardInfoDisplayText';
import { DashboardInfoSmallText } from '@ui/atoms/DashboardInfoSmallText';
import FlexColumn from '@ui/atoms/FlexColumn';
import Pill from '@ui/atoms/Pill';
import TextLink from '@ui/atoms/TextLink';

import {
	type IBillingPageContent,
	IBillingPageInformation,
} from '@ui/routes/Dashboard/Billing/BillingInfo/Billing.component';

import { MembershipSubscription, SubscriptionPaidFor } from '@hooks/useMemberInfo';

import { interpolate } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';

import { FolxMembershipStatus } from '@data/membership';

import { ColorTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

import { SectionLabel } from './Shared.component';

export interface FolxMembershipCardProperties {
	status: FolxMembershipStatus;
	upcomingInvoice?: {
		nextPaymentAttempt?: Date;
		amount: number;
	};
	lastInvoice?: {
		date?: Date;
		amount: number;
	};
	employer?: string;
	canceledAt?: Date;
	cancelAt?: Date;
	nextSubscriptionRenewalDate?: Date;
	paidFor?: keyof typeof SubscriptionPaidFor;
}

export interface FolxMembershipCardProps {
	cardContent: IBillingPageContent['folxMembership'];
	isMembershipWaivable: boolean;
	b2bCustomer?: IBillingPageInformation['authenticatedMember']['b2b'];
	invoices?: IBillingPageInformation['authenticatedMember']['invoices'];
	membershipSubscription?: MembershipSubscription;
	protectedNavigate?: ((intent?: string) => Promise<void>) | (() => void);
}

interface CardContent {
	title: string | null;
	amount: string | null;
	badge: string | null;
	cta: string | null;
	date: string | null;
	description: string | null;
	eyebrow: string;
	text: string | null;
	secondaryCta: string | null;
}

const Price = styled.span`
	font-weight: bold;
`;

const SecondaryCta = styled(TextLink)`
	font-size: ${theme.fontSizes.body.sm};
`;

const MembershipInfoDisplay = styled(FlexColumn)`
	position: relative;
	padding: ${theme.spacing.md};
	gap: ${theme.spacing.smmd};
	width: 100%;
	min-height: 14.8rem;
`;

const priceFormatter = new Intl.NumberFormat('en-US', {
	currency: 'USD',
	minimumFractionDigits: 2,
	style: 'currency',
}).format;

const formatDate = (date: Date | undefined, format = 'DD') =>
	date
		? DateTime.fromJSDate(date).toFormat(format, {
				locale: 'en',
		  })
		: null;

const lessThanSixWeeksFromNow = (date: Date | undefined) => {
	if (!date) return false;

	const now = DateTime.now();
	const next = DateTime.fromJSDate(date);
	return next.diff(now).as('weeks') < 6;
};

const getCardContent = (
	content: IBillingPageContent['folxMembership'],
	membershipProperties: FolxMembershipCardProperties,
	isMembershipWaivable: boolean,
	billingInterval: 'MONTH' | 'YEAR' | 'NONE'
): CardContent => {
	const membershipStatus = membershipProperties.status;

	// initialization
	let amount: string | null = null;
	let title: string | null = null;
	let badge: string | null = content.badgeCanceled;
	let cta: string | null = content.ctaCanceled;
	let date: string | null = formatDate(membershipProperties.lastInvoice?.date);
	let description: string | null = null;
	let eyebrow = content.eyebrowCanceled;
	let text: string | null = null;
	let secondaryCta: string | null = null;

	// status changes
	if (membershipStatus === FolxMembershipStatus.ACTIVE) {
		amount =
			membershipProperties.upcomingInvoice && billingInterval === 'MONTH'
				? priceFormatter(membershipProperties.upcomingInvoice.amount)
				: null;
		badge = content.badgeActive;
		cta = content.ctaActive;
		date = formatDate(membershipProperties.upcomingInvoice?.nextPaymentAttempt);
		eyebrow = content.eyebrowActive;

		if (billingInterval === 'YEAR') {
			if (lessThanSixWeeksFromNow(membershipProperties.nextSubscriptionRenewalDate)) {
				secondaryCta = content.secondaryCtaSwitchToMonthly;
			} else {
				cta = null;
			}
			title = content.titleYearly;
			eyebrow = content.eyebrowActiveYearly;
		} else if (billingInterval === 'MONTH') {
			title = content.titleMonthly;

			if (!isMembershipWaivable) {
				secondaryCta = content.secondaryCtaSwitchToYearly;
			}
		}
	} else if (membershipStatus === FolxMembershipStatus.PAST_DUE) {
		badge = content.badgePastDue;
		cta = content.ctaPastDue;
		date = null;
		description = membershipProperties.lastInvoice?.date
			? interpolate(content.descriptionPastDue, {
					dateLastPaymentAttempt: formatDate(membershipProperties.lastInvoice.date),
			  })
			: null;
		eyebrow = content.eyebrowPastDue;
		text = content.textPastDue;
		secondaryCta = content.secondaryCtaPastDue;
	} else if (
		[FolxMembershipStatus.CANCELING, FolxMembershipStatus.CANCELED].includes(membershipStatus)
	) {
		if (membershipStatus === FolxMembershipStatus.CANCELING) {
			description = membershipProperties.cancelAt
				? interpolate(content.descriptionCanceled, {
						lastDayMembership: formatDate(membershipProperties.cancelAt),
				  })
				: null;
		}

		if (membershipStatus === FolxMembershipStatus.CANCELED) description = null;

		title = null;
		date = formatDate(membershipProperties.canceledAt);
	}

	// "waived" override
	if (exists(membershipProperties.employer)) {
		badge = null;
		cta = null;
		date = null;
		description = interpolate(content.descriptionB2b, {
			b2bCompany:
				membershipProperties.employer?.toLocaleLowerCase() === 'folx'
					? 'FOLX'
					: membershipProperties.employer,
		});
		eyebrow = interpolate(content.eyebrowB2b, { b2bCompany: membershipProperties.employer });
		text = membershipProperties.employer === 'folx' ? content.textB2b : null;
	}

	return {
		title,
		amount,
		badge,
		cta,
		date,
		description,
		eyebrow,
		text,
		secondaryCta,
	};
};

const mapStatusToColor: Record<FolxMembershipStatus, ColorTypes> = {
	[FolxMembershipStatus.ACTIVE]: 'green',
	[FolxMembershipStatus.CANCELED]: 'lightRed',
	[FolxMembershipStatus.CANCELING]: 'lightRed',
	[FolxMembershipStatus.PAST_DUE]: 'yellow',
	[FolxMembershipStatus.UNPAID]: 'yellow',
	[FolxMembershipStatus.UNKNOWN]: 'gray',
	[FolxMembershipStatus.INCOMPLETE]: 'gray',
	[FolxMembershipStatus.EXPIRED]: 'gray',
};

const routes = {
	cancel: './cancel_membership',
	cancelPastDue: './cancel_membership?isPastDue=true',
	reactivatePending: '../reactivate_membership/summary?backTo=billing',
	reactivateCanceled: '../reactivate_membership/summary?backTo=billing',
	billPay: './bills',
	switchToYearlyMembership: './switch_to_yearly_membership',
	switchToMonthlyMembership: './switch_to_monthly_membership',
} as const;

const parseMembershipInformation = ({
	b2bCustomer,
	invoices,
	membershipSubscription,
}: Pick<
	FolxMembershipCardProps,
	'b2bCustomer' | 'invoices' | 'membershipSubscription'
>): FolxMembershipCardProperties => {
	const upcomingInvoice = {
		amount: (membershipSubscription?.upcomingInvoice?.total ?? 0) / 100,
		nextPaymentAttempt: membershipSubscription?.upcomingInvoice?.nextPaymentAttempt
			? new Date(membershipSubscription?.upcomingInvoice?.nextPaymentAttempt)
			: undefined,
	};

	const lastInvoice = invoices?.nodes?.[0]
		? {
				amount: (invoices?.nodes?.[0].total ?? 0) / 100,
				created: invoices?.nodes?.[0].created ? new Date(invoices?.nodes?.[0].created) : undefined,
		  }
		: undefined;

	let membershipStatus = membershipSubscription?.status
		? FolxMembershipStatus[membershipSubscription?.status]
		: FolxMembershipStatus.UNKNOWN;

	// a "pending cancellation" user will have `cancelAt` and `canceledAt`. but it will be "ACTIVE"
	if (
		membershipStatus === FolxMembershipStatus.ACTIVE &&
		membershipSubscription?.cancelAt &&
		membershipSubscription?.canceledAt
	)
		membershipStatus = FolxMembershipStatus.CANCELING;

	return {
		status: membershipStatus,
		employer: b2bCustomer?.customer?.name,
		lastInvoice,
		upcomingInvoice,
		cancelAt: membershipSubscription?.cancelAt
			? new Date(membershipSubscription.cancelAt)
			: undefined,
		canceledAt: membershipSubscription?.canceledAt
			? new Date(membershipSubscription.canceledAt)
			: undefined,
		nextSubscriptionRenewalDate: membershipSubscription?.nextSubscriptionRenewalDate
			? new Date(membershipSubscription.nextSubscriptionRenewalDate)
			: undefined,
		paidFor: membershipSubscription?.paidFor
			? SubscriptionPaidFor[membershipSubscription.paidFor]
			: undefined,
	};
};

const FolxMembershipCard: FC<FolxMembershipCardProps> = ({
	cardContent,
	b2bCustomer,
	invoices,
	isMembershipWaivable,
	membershipSubscription,
	protectedNavigate = () => {
		return;
	},
}) => {
	const membershipProperties = useMemo(
		() =>
			parseMembershipInformation({
				b2bCustomer,
				invoices,
				membershipSubscription,
			}),
		[b2bCustomer, invoices, membershipSubscription]
	);

	const status = membershipProperties.status;
	const paidFor = membershipProperties.paidFor;
	const nextSubscriptionRenewalDate = membershipProperties.nextSubscriptionRenewalDate;

	const isMonthlySubscription = membershipSubscription?.billingInterval === 'MONTH';
	const isMembershipWaived =
		(paidFor !== SubscriptionPaidFor.OUT_OF_POCKET && exists(paidFor)) || isMembershipWaivable;

	const onClick = useCallback(() => {
		if (status === FolxMembershipStatus.ACTIVE) {
			protectedNavigate(routes.cancel);
		} else if (status === FolxMembershipStatus.CANCELING) {
			protectedNavigate(routes.reactivatePending);
		} else if (status === FolxMembershipStatus.CANCELED) {
			protectedNavigate(routes.reactivateCanceled);
		} else if (status === FolxMembershipStatus.PAST_DUE) {
			protectedNavigate(routes.billPay);
		} else {
			console.error('CTA should not be visible');
			protectedNavigate();
		}
	}, [protectedNavigate, status]);

	const secondaryCtaLink = useMemo(() => {
		if (status === FolxMembershipStatus.PAST_DUE) {
			return routes.cancelPastDue;
		} else if (status === FolxMembershipStatus.ACTIVE && !isMembershipWaived) {
			if (isMonthlySubscription) {
				return routes.switchToYearlyMembership;
			} else if (lessThanSixWeeksFromNow(nextSubscriptionRenewalDate)) {
				return routes.switchToMonthlyMembership;
			}
		}
		return '';
	}, [isMonthlySubscription, isMembershipWaived, nextSubscriptionRenewalDate, status]);

	const { title, amount, badge, cta, date, description, eyebrow, text, secondaryCta } =
		getCardContent(
			cardContent,
			membershipProperties,
			isMembershipWaived,
			membershipSubscription?.billingInterval ?? 'NONE'
		);

	return (
		<div>
			<SectionLabel>FOLX membership</SectionLabel>
			<BaseCard bgColor="white" borderless radius="md" shadow>
				<MembershipInfoDisplay>
					<FlexColumn gap="xs">
						<CardRow>
							<DashboardInfoSmallText variant="field">
								{title ? title : eyebrow}
							</DashboardInfoSmallText>
							{badge && <Pill color={mapStatusToColor[status]} title={badge} />}
						</CardRow>
						{title && <DashboardInfoSmallText variant="field">{eyebrow}</DashboardInfoSmallText>}
						{date && <DashboardInfoDisplayText>{date}</DashboardInfoDisplayText>}
						{amount && !isMembershipWaived && (
							<DashboardInfoDisplayText>
								<Price>{amount}</Price>
							</DashboardInfoDisplayText>
						)}
						{description && <DashboardInfoDisplayText>{description}</DashboardInfoDisplayText>}
						{text && <DashboardInfoDisplayText>{text}</DashboardInfoDisplayText>}
					</FlexColumn>
					{(cta || secondaryCta) && (
						<FlexColumn gap="sm">
							{cta && <Button disabled={false} label={cta} onClick={onClick} />}
							{secondaryCta && (
								<SecondaryCta variant="normal" label={secondaryCta} href={secondaryCtaLink} />
							)}
						</FlexColumn>
					)}
				</MembershipInfoDisplay>
			</BaseCard>
		</div>
	);
};

export default FolxMembershipCard;
